import { useEffect, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Truck, Plus, Trash2, Pencil, MapPin, Globe2, Building2, GitBranch } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import MasterTable from "@/components/MasterTable";

const emptyForm = { name: "", scope: "tenant", tracking_url_template: "", active: true, branch_ids: [], tenant_ids: [] };

export default function CouriersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  // Per-tenant branch allocation for company_admin editing platform courier
  const [myBranchAlloc, setMyBranchAlloc] = useState([]);

  const isSuper = user?.role === "super_admin";
  const isCompany = user?.role === "company_admin";
  const myTenantId = user?.tenant_id;

  const load = async () => {
    const calls = [api.get("/couriers/manage"), api.get("/branches")];
    if (isSuper) calls.push(api.get("/tenants"));
    const [c, b, t] = await Promise.all(calls);
    setItems(c.data);
    setBranches(b.data);
    if (t) setTenants(t.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, scope: isSuper ? "platform" : "tenant" });
    setMyBranchAlloc([]);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      scope: c.scope,
      tracking_url_template: c.tracking_url_template || "",
      active: c.active !== false,
      branch_ids: c.branch_ids || [],
      tenant_ids: c.tenant_ids || [],
    });
    // For company_admin editing platform courier — load THEIR tenant's allocation slot
    const mine = (c.allocations && c.allocations[myTenantId]) || [];
    setMyBranchAlloc([...mine]);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        // Two edit modes:
        // (a) super_admin editing any courier → full field set
        // (b) company_admin editing a PLATFORM courier → only allocations for their tenant
        // (c) company_admin editing a TENANT courier of theirs → name/active/url/branch_ids
        const editingPlatform = editing.scope === "platform";
        let payload;
        if (isCompany && editingPlatform) {
          payload = {
            allocations: { [myTenantId]: myBranchAlloc.length ? myBranchAlloc : [] },
          };
        } else {
          payload = {
            name: form.name,
            active: form.active,
            tracking_url_template: form.tracking_url_template,
          };
          if (editingPlatform) {
            payload.tenant_ids = form.tenant_ids?.length ? form.tenant_ids : null;
          } else {
            payload.branch_ids = form.branch_ids?.length ? form.branch_ids : null;
          }
        }
        await api.patch(`/couriers/${editing.id}`, payload);
        toast.success("Courier updated");
      } else {
        const payload = {
          name: form.name.trim(),
          scope: form.scope,
          active: form.active,
          tracking_url_template: form.tracking_url_template,
          branch_ids: form.scope === "tenant" && form.branch_ids?.length ? form.branch_ids : null,
          tenant_ids: form.scope === "platform" && form.tenant_ids?.length ? form.tenant_ids : null,
        };
        await api.post("/couriers", payload);
        toast.success("Courier created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete courier "${c.name}"?`)) return;
    try {
      await api.delete(`/couriers/${c.id}`);
      toast.success("Courier deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggle = (list, id) => list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  // Every row is now editable — company_admins get "Allocate" on platform couriers, "Edit" on their own tenant couriers
  const canEditRow = (c) => {
    if (isSuper) return true;
    if (isCompany && c.scope === "platform") return true; // for allocation
    if (isCompany && c.scope === "tenant" && c.tenant_id === myTenantId) return true;
    return false;
  };
  const canDeleteRow = (c) => {
    if (isSuper) return true;
    if (isCompany && c.scope === "tenant" && c.tenant_id === myTenantId) return true;
    return false;
  };

  const branchesLabel = (c) => {
    if (c.scope === "platform") {
      const tCount = (c.tenant_ids || []).length;
      const allocCount = Object.keys(c.allocations || {}).length;
      if (!tCount && !allocCount) return "All tenants · all branches";
      const parts = [];
      if (tCount) parts.push(`${tCount} tenant(s)`);
      if (allocCount) parts.push(`${allocCount} tenant custom allocation`);
      return parts.join(" · ");
    }
    return c.branch_ids && c.branch_ids.length ? `${c.branch_ids.length} branch(es)` : "All branches";
  };

  const editingPlatform = editing?.scope === "platform";
  const companyEditingPlatform = isCompany && editingPlatform;

  const rows = items.map((c) => ({ ...c, allocation_label: branchesLabel(c) }));

  const columns = [
    { key: "name", label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "scope", label: "Scope", render: (r) => (
        <span className={`mono text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded inline-flex items-center gap-1 ${
          r.scope === "platform" ? "border-primary text-primary bg-red-50" : "border-slate-400 text-slate-700 bg-slate-50"
        }`}>
          {r.scope === "platform" ? <Globe2 size={10} /> : <MapPin size={10} />}
          {r.scope}
        </span>
    )},
    { key: "allocation_label", label: "Allocation", className: "text-xs text-muted-foreground" },
    { key: "tracking_url_template", label: "Track URL", className: "text-xs mono truncate max-w-[220px]",
      render: (r) => r.tracking_url_template || "—" },
    { key: "active", label: "Status", render: (r) => (
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${r.active !== false ? "bg-green-50 text-green-700 border border-green-200" : "bg-slate-50 text-slate-500 border border-slate-200"}`}>
          {r.active !== false ? "Active" : "Inactive"}
        </span>
    )},
    { key: "actions", label: "", sortable: false, className: "text-right whitespace-nowrap", render: (r) => (
        <div className="inline-flex items-center gap-2">
          {canEditRow(r) && (
            <button data-testid={`courier-edit-${r.id}`} onClick={() => openEdit(r)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:border-primary hover:bg-primary hover:text-primary-foreground transition-colors font-semibold"
              title={isCompany && r.scope === "platform" ? "Allocate to your branches" : "Edit courier"}>
              <Pencil size={12} /> {isCompany && r.scope === "platform" ? "Allocate" : "Edit"}
            </button>
          )}
          {canDeleteRow(r) && (
            <button data-testid={`courier-delete-${r.id}`} onClick={() => remove(r)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:border-primary hover:bg-primary hover:text-primary-foreground transition-colors font-semibold"
              title="Delete">
              <Trash2 size={12} /> Delete
            </button>
          )}
        </div>
    )},
  ];

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><Truck size={12} /> Courier management</div>
          <h1 className="text-4xl font-display mt-1">Couriers.</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isSuper
              ? "Manage platform-wide couriers, allocate them to specific tenants, and edit tenant-specific couriers."
              : "Add tenant couriers, allocate platform couriers to specific branches, and disable ones you don't use."}
          </p>
        </div>
        <Button data-testid="courier-new-btn" onClick={openNew}
          className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
          <Plus size={14} className="mr-2" /> Add courier
        </Button>
      </header>

      <MasterTable
        columns={columns}
        rows={rows}
        searchKeys={["name", "scope", "allocation_label", "tracking_url_template"]}
        getRowKey={(r) => r.id}
        testidPrefix="couriers"
        defaultSort={{ key: "name", dir: "asc" }}
        emptyText="No couriers yet. Add your first courier to enable it in inward/outward flows."
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="courier-form-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing
                ? (companyEditingPlatform ? "Allocate courier to your branches" : "Edit courier")
                : "Add courier"}
            </DialogTitle>
            <p id="courier-form-desc" className="text-xs text-muted-foreground">
              {companyEditingPlatform
                ? `${editing.name} is a platform-wide courier. Select which of your branches may use it, or leave empty to enable on all your branches.`
                : (editing ? "Update settings and allocation." : "Create a new courier for inward/outward entries.")}
            </p>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {/* Company admin allocating a platform courier — only branch chooser */}
            {companyEditingPlatform ? (
              <BranchAllocator branches={branches} selected={myBranchAlloc} setSelected={setMyBranchAlloc}
                testIdPrefix="alloc-branch" />
            ) : (
              <>
                <label className="block">
                  <span className="overline">Name <span className="text-primary">*</span></span>
                  <Input data-testid="courier-name-input" value={form.name} required
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 rounded-md border-input focus-visible:ring-primary" />
                </label>

                {!editing && isSuper && (
                  <label className="block">
                    <span className="overline">Scope</span>
                    <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                      <SelectTrigger data-testid="courier-scope" className="rounded-md border-input mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="platform">Platform (visible to selected tenants — or all)</SelectItem>
                        <SelectItem value="tenant">Tenant (this company only)</SelectItem>
                      </SelectContent>
                    </Select>
                  </label>
                )}

                <label className="block">
                  <span className="overline">Tracking URL template (optional)</span>
                  <Input data-testid="courier-url-input" value={form.tracking_url_template}
                    placeholder="https://example.com/track?id=%s"
                    onChange={(e) => setForm({ ...form, tracking_url_template: e.target.value })}
                    className="mt-1 rounded-md border-input focus-visible:ring-primary font-mono text-xs" />
                  <div className="text-[10px] text-muted-foreground mt-1">Use %s where the AWB / tracking number should be inserted.</div>
                </label>

                {/* Platform scope + super_admin: tenant allocation */}
                {form.scope === "platform" && isSuper && (
                  <div>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="overline flex items-center gap-1.5"><Building2 size={11} /> Tenant allocation (optional)</span>
                      <BulkActions
                        count={form.tenant_ids?.length || 0}
                        total={tenants.length}
                        onAll={() => setForm({ ...form, tenant_ids: tenants.map((t) => t.id) })}
                        onClear={() => setForm({ ...form, tenant_ids: [] })}
                        onInvert={() => {
                          const cur = form.tenant_ids || [];
                          setForm({ ...form, tenant_ids: tenants.map((t) => t.id).filter((id) => !cur.includes(id)) });
                        }}
                        prefix="courier-tenant-bulk"
                      />
                    </div>
                    <div className="mt-1 border border-border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                      {tenants.length > 0 && (
                        <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/40 p-1 rounded border-b border-dashed border-border mb-1 font-semibold text-primary">
                          <input type="checkbox" data-testid="courier-tenant-all"
                            checked={tenants.length > 0 && (form.tenant_ids?.length || 0) === tenants.length}
                            ref={(el) => { if (el) el.indeterminate = (form.tenant_ids?.length || 0) > 0 && (form.tenant_ids?.length || 0) < tenants.length; }}
                            onChange={() => {
                              const all = (form.tenant_ids?.length || 0) === tenants.length;
                              setForm({ ...form, tenant_ids: all ? [] : tenants.map((t) => t.id) });
                            }}
                            className="accent-[#BE1E1E]" />
                          <span>{(form.tenant_ids?.length || 0) === tenants.length ? "Deselect all" : "Select all tenants"}</span>
                          <span className="text-[10px] text-muted-foreground ml-auto">{form.tenant_ids?.length || 0}/{tenants.length}</span>
                        </label>
                      )}
                      {tenants.map((t) => (
                        <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/40 p-1 rounded">
                          <input type="checkbox" data-testid={`courier-tenant-${t.code}`}
                            checked={form.tenant_ids?.includes(t.id) || false}
                            onChange={() => setForm({ ...form, tenant_ids: toggle(form.tenant_ids || [], t.id) })}
                            className="accent-[#BE1E1E]" />
                          <span>{t.name}</span>
                          <span className="mono text-[10px] text-muted-foreground ml-auto">{t.code}</span>
                        </label>
                      ))}
                      {tenants.length === 0 && <div className="text-[11px] text-muted-foreground p-1">No tenants yet.</div>}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">Leave empty to make available to all tenants. Use All/Invert/Clear to bulk-allocate. Company admins can further restrict to specific branches.</div>
                  </div>
                )}

                {/* Tenant scope: branch allocation */}
                {form.scope === "tenant" && branches.length > 0 && (
                  <BranchAllocator branches={branches}
                    selected={form.branch_ids || []}
                    setSelected={(next) => setForm({ ...form, branch_ids: next })}
                    testIdPrefix="courier-branch" />
                )}

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="courier-active" checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="accent-[#BE1E1E]" />
                  <span>Active (available in inward/outward dropdowns)</span>
                </label>
              </>
            )}

            <Button data-testid="courier-submit" type="submit"
              className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
              {editing ? "Save changes" : "Create courier"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BranchAllocator({ branches, selected, setSelected, testIdPrefix }) {
  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const allIds = branches.map((b) => b.id);
  const allChecked = branches.length > 0 && selected.length === branches.length;
  const someChecked = selected.length > 0 && !allChecked;
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="overline flex items-center gap-1.5"><GitBranch size={11} /> Branch allocation (optional)</span>
        <BulkActions
          count={selected.length}
          total={branches.length}
          onAll={() => setSelected([...allIds])}
          onClear={() => setSelected([])}
          onInvert={() => setSelected(allIds.filter((id) => !selected.includes(id)))}
          prefix={`${testIdPrefix}-bulk`}
        />
      </div>
      <div className="mt-1 border border-border rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
        {branches.length === 0 && <div className="text-[11px] text-muted-foreground p-1">No branches yet.</div>}
        {branches.length > 0 && (
          <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/40 p-1 rounded border-b border-dashed border-border mb-1 font-semibold text-primary">
            <input type="checkbox" data-testid={`${testIdPrefix}-all`}
              checked={allChecked}
              ref={(el) => { if (el) el.indeterminate = someChecked; }}
              onChange={() => setSelected(allChecked ? [] : [...allIds])}
              className="accent-[#BE1E1E]" />
            <span>{allChecked ? "Deselect all" : someChecked ? "Select all remaining" : "Select all branches"}</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{selected.length}/{branches.length}</span>
          </label>
        )}
        {branches.map((b) => (
          <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/40 p-1 rounded">
            <input type="checkbox" data-testid={`${testIdPrefix}-${b.id}`}
              checked={selected.includes(b.id)}
              onChange={() => toggle(b.id)}
              className="accent-[#BE1E1E]" />
            <span>{b.name}</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{b.location}</span>
          </label>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">
        Leave empty to allow all branches. Ticked = only these branches can pick this courier in inward/outward.
      </div>
    </div>
  );
}

function BulkActions({ count, total, onAll, onClear, onInvert, prefix }) {
  if (total === 0) return null;
  const btn = "text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border border-border hover:border-primary hover:text-primary transition-colors";
  return (
    <div className="flex items-center gap-1.5" data-testid={prefix}>
      <button type="button" onClick={onAll} data-testid={`${prefix}-all`} className={btn}>All</button>
      <button type="button" onClick={onInvert} data-testid={`${prefix}-invert`} className={btn}>Invert</button>
      <button type="button" onClick={onClear} data-testid={`${prefix}-clear`} className={btn} disabled={count === 0}>Clear</button>
    </div>
  );
}
